import { useState, useEffect, useRef, useCallback } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import {
  LayoutDashboard, UtensilsCrossed, ShoppingBag, Users, BarChart3,
  Image, Settings, LogOut, ExternalLink, FolderTree, User, Tag,
  Bell, X, Volume2, MessageCircle, Menu as MenuIcon, Smartphone, Clock
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Logo } from '../../components/Logo';

const navItems = [
  { path: '/admin/dashboard', label: 'Dashboard', icon: LayoutDashboard },
  { path: '/admin/menu', label: 'Menu', icon: UtensilsCrossed },
  { path: '/admin/categories', label: 'Categories', icon: FolderTree },
  { path: '/admin/orders', label: 'Orders', icon: ShoppingBag },
  { path: '/admin/customers', label: 'Customers', icon: Users },
  { path: '/admin/analytics', label: 'Analytics', icon: BarChart3 },
  { path: '/admin/promo-codes', label: 'Promo Codes', icon: Tag },
  { path: '/admin/notifications', label: 'Notifications', icon: Bell },
  { path: '/admin/media', label: 'Media', icon: Image },
  { path: '/admin/settings', label: 'Settings', icon: Settings },
];

function createNotificationSound() {
  try {
    const ctx = new (window.AudioContext || window.webkitAudioContext)();
    const playTone = (freq, startTime, duration) => {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.type = 'sine';
      osc.frequency.setValueAtTime(freq, startTime);
      gain.gain.setValueAtTime(0.3, startTime);
      gain.gain.exponentialRampToValueAtTime(0.01, startTime + duration);
      osc.start(startTime);
      osc.stop(startTime + duration);
    };
    return () => {
      const now = ctx.currentTime;
      playTone(523, now, 0.15);
      playTone(659, now + 0.18, 0.15);
      playTone(784, now + 0.36, 0.25);
      playTone(523, now + 0.8, 0.15);
      playTone(659, now + 0.98, 0.15);
      playTone(784, now + 1.16, 0.25);
    };
  } catch (e) {
    return () => {};
  }
}

export default function AdminLayout({ children, title, subtitle }) {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout, token } = useAuth();
  const [notifications, setNotifications] = useState([]);
  const [showNotifPanel, setShowNotifPanel] = useState(false);
  const [orderPopup, setOrderPopup] = useState(null);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [pushEnabled, setPushEnabled] = useState(false);
  const [pushLoading, setPushLoading] = useState(false);
  const playSoundRef = useRef(null);

  useEffect(() => {
    const initAudio = () => { if (!playSoundRef.current) playSoundRef.current = createNotificationSound(); document.removeEventListener('click', initAudio); };
    document.addEventListener('click', initAudio);
    playSoundRef.current = createNotificationSound();
    return () => document.removeEventListener('click', initAudio);
  }, []);

  const playSound = useCallback(() => { if (playSoundRef.current) playSoundRef.current(); }, []);

  useEffect(() => {
    if ("Notification" in window && Notification.permission === "default") Notification.requestPermission();
  }, []);

  // Check if push is already enabled
  useEffect(() => {
    if (!('serviceWorker' in navigator) || !('PushManager' in window)) return;
    navigator.serviceWorker.getRegistration('/sw.js').then(reg => {
      if (reg) reg.pushManager.getSubscription().then(sub => { if (sub) setPushEnabled(true); });
    }).catch(() => {});
  }, []);

  const enablePushNotifications = async () => {
    if (!('serviceWorker' in navigator) || !('PushManager' in window)) return;
    setPushLoading(true);
    try {
      const permission = await Notification.requestPermission();
      if (permission !== 'granted') { setPushLoading(false); return; }

      const registration = await navigator.serviceWorker.register('/sw.js');
      await navigator.serviceWorker.ready;

      const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
      const keyRes = await fetch(`${API}/push/public-key`);
      const { publicKey } = await keyRes.json();

      function base64UrlToUint8Array(b64) {
        const padding = '='.repeat((4 - (b64.length % 4)) % 4);
        const base64 = (b64 + padding).replace(/-/g, '+').replace(/_/g, '/');
        return Uint8Array.from(atob(base64), c => c.charCodeAt(0));
      }

      let subscription = await registration.pushManager.getSubscription();
      if (!subscription) {
        subscription = await registration.pushManager.subscribe({
          userVisibleOnly: true,
          applicationServerKey: base64UrlToUint8Array(publicKey),
        });
      }

      await fetch(`${API}/push/subscribe`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify(subscription.toJSON()),
      });
      setPushEnabled(true);
    } catch (e) {
      console.error('Push subscription failed:', e);
    }
    setPushLoading(false);
  };

  // Close sidebar on navigation (mobile)
  useEffect(() => { setSidebarOpen(false); }, [location.pathname]);

  // WebSocket
  useEffect(() => {
    const wsUrl = process.env.REACT_APP_BACKEND_URL?.replace('https://', 'wss://').replace('http://', 'ws://');
    if (!wsUrl || !token) return;
    let ws = null, reconnectTimeout = null, pingInterval = null;
    let mounted = true;

    const connect = () => {
      if (!mounted) return;
      try {
        ws = new WebSocket(`${wsUrl}/api/ws`);
      } catch (e) {
        if (mounted) reconnectTimeout = setTimeout(connect, 3000);
        return;
      }
      ws.onopen = () => {
        if (!mounted) { ws.close(); return; }
        pingInterval = setInterval(() => {
          if (ws?.readyState === WebSocket.OPEN) {
            try { ws.send('ping'); } catch (_) {}
          }
        }, 15000);
      };
      ws.onmessage = (event) => {
        if (!mounted || event.data === 'pong') return;
        try {
          const data = JSON.parse(event.data);
          if (data.type === 'new_order') {
            const order = data.order;
            const notif = {
              id: Date.now(), order_number: order?.order_number, order_id: order?.id,
              total: order?.total, customer: order?.customer_name, customer_phone: order?.customer_phone,
              items_count: order?.items?.length || 0, items: order?.items || [],
              address: order?.customer_address, payment_method: order?.payment_method,
              timestamp: new Date().toISOString(),
              created_at: new Date().toISOString(),
              time: new Date().toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit', hour12: true }),
              whatsapp_url: order?.whatsapp_notify_url || null, read: false,
            };
            setNotifications(prev => {
              const updated = [notif, ...prev].slice(0, 50);
              try { const stored = JSON.parse(localStorage.getItem('sizzlebox_notifications') || '[]'); localStorage.setItem('sizzlebox_notifications', JSON.stringify([notif, ...stored].slice(0, 200))); } catch (_) {}
              return updated;
            });
            playSound();
            setOrderPopup(notif);
            setTimeout(() => setOrderPopup(prev => prev?.id === notif.id ? null : prev), 30000);
            if ("Notification" in window && Notification.permission === "granted") {
              try { new Notification('New Order!', { body: `#${order?.order_number} - ₦${order?.total?.toLocaleString()} from ${order?.customer_name}`, tag: `order-${order?.id}`, requireInteraction: true }); } catch (_) {}
            }
          }
        } catch (_) {}
      };
      ws.onclose = () => {
        clearInterval(pingInterval);
        if (mounted) reconnectTimeout = setTimeout(connect, 2000);
      };
      ws.onerror = () => { clearInterval(pingInterval); ws?.close(); };
    };
    connect();
    return () => { mounted = false; clearTimeout(reconnectTimeout); clearInterval(pingInterval); if (ws?.readyState === WebSocket.OPEN || ws?.readyState === WebSocket.CONNECTING) ws.close(); };
  }, [token, playSound]);

  const handleLogout = () => { logout(); navigate('/admin/login'); };
  const unreadCount = notifications.filter(n => !n.read).length;
  const markAllRead = () => setNotifications(prev => prev.map(n => ({ ...n, read: true })));
  const dismissNotif = (id) => setNotifications(prev => prev.filter(n => n.id !== id));

  const formatNotifTime = (notif) => {
    const ts = notif.timestamp || notif.created_at;
    if (ts) {
      const d = new Date(ts);
      const now = new Date();
      const diffMs = now - d;
      const diffMins = Math.floor(diffMs / 60000);
      if (diffMins < 1) return 'Just now';
      if (diffMins < 60) return `${diffMins}m ago`;
      const diffHours = Math.floor(diffMins / 60);
      if (diffHours < 24) return `${diffHours}h ago`;
      const diffDays = Math.floor(diffHours / 24);
      if (diffDays < 7) return `${diffDays}d ago`;
      return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    }
    return notif.time || '';
  };

  const formatFullTimestamp = (notif) => {
    const ts = notif.timestamp || notif.created_at;
    if (ts) {
      return new Date(ts).toLocaleString('en-US', {
        month: 'short', day: 'numeric', year: 'numeric',
        hour: 'numeric', minute: '2-digit', second: '2-digit', hour12: true
      });
    }
    return notif.time || '';
  };

  return (
    <div className="min-h-screen bg-black" data-testid="admin-layout">
      {/* Mobile overlay */}
      {sidebarOpen && (
        <div className="fixed inset-0 bg-black/60 z-40 lg:hidden" onClick={() => setSidebarOpen(false)} data-testid="mobile-sidebar-overlay" />
      )}

      {/* Sidebar */}
      <aside className={`fixed h-screen bg-[#1A1A1A] border-r border-white/10 flex flex-col overflow-hidden z-50 transition-transform duration-300 w-64 lg:w-60 ${sidebarOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}`}>
        <div className="px-5 py-4 border-b border-white/10 flex-shrink-0 flex items-center justify-between">
          <Link to="/admin/dashboard" className="flex items-center gap-2"><Logo className="h-9" /></Link>
          <button className="lg:hidden text-gray-400 hover:text-white" onClick={() => setSidebarOpen(false)}><X className="w-5 h-5" /></button>
        </div>

        <nav className="flex-1 overflow-y-auto px-3 py-3" style={{ scrollbarWidth: 'thin', scrollbarColor: '#333 transparent' }}>
          <p className="text-gray-500 text-xs uppercase tracking-wider mb-3 px-3">MAIN</p>
          <ul className="space-y-0.5">
            {navItems.map(item => {
              const Icon = item.icon;
              const isActive = location.pathname === item.path;
              return (
                <li key={item.path}>
                  <Link to={item.path} className={`flex items-center gap-3 px-3 py-2.5 rounded-lg transition-all text-sm ${isActive ? 'bg-fire-500 text-white' : 'text-gray-400 hover:text-white hover:bg-white/5'}`} data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}>
                    <Icon className="w-4 h-4 flex-shrink-0" />{item.label}
                  </Link>
                </li>
              );
            })}
          </ul>
          <div className="mt-6">
            <p className="text-gray-500 text-xs uppercase tracking-wider mb-3 px-3">QUICK ACTIONS</p>
            <Link to="/" target="_blank" className="flex items-center gap-3 px-3 py-2 rounded-lg bg-fire-500/10 text-fire-500 hover:bg-fire-500/20 transition-colors text-sm">
              <ExternalLink className="w-4 h-4 flex-shrink-0" />View Live Site
            </Link>
          </div>
        </nav>

        <div className="px-3 py-3 border-t border-white/10 flex-shrink-0">
          <div className="flex items-center gap-2 mb-2">
            <div className="w-8 h-8 rounded-full bg-fire-500/20 flex items-center justify-center flex-shrink-0"><User className="w-4 h-4 text-fire-500" /></div>
            <div className="min-w-0">
              <p className="text-white font-semibold text-xs truncate">{user?.name || 'Admin'}</p>
              <p className="text-gray-500 text-xs truncate">{user?.email || 'admin@sizzlebox.ng'}</p>
            </div>
          </div>
          <Button variant="ghost" onClick={handleLogout} className="w-full justify-start text-gray-400 hover:text-red-400 hover:bg-red-500/10 h-8 text-xs" data-testid="sidebar-logout-btn">
            <LogOut className="w-3.5 h-3.5 mr-2" />Sign Out
          </Button>
        </div>
      </aside>

      {/* Main Content */}
      <main className="lg:ml-60">
        {/* Header */}
        <header className="bg-[#0a0a0a] border-b border-white/10 px-4 md:px-8 py-3 md:py-4 sticky top-0 z-30">
          <div className="flex justify-between items-center">
            <div className="flex items-center gap-3">
              <button className="lg:hidden p-2 rounded-lg hover:bg-white/5 text-gray-400" onClick={() => setSidebarOpen(true)} data-testid="mobile-menu-btn">
                <MenuIcon className="w-6 h-6" />
              </button>
              <div>
                <h1 className="font-heading text-xl md:text-2xl text-white">{title}</h1>
                {subtitle && <p className="text-gray-400 text-xs md:text-sm mt-0.5 hidden sm:block">{subtitle}</p>}
              </div>
            </div>
            <div className="flex items-center gap-1 md:gap-3">
              {/* Notification Bell */}
              <div className="relative">
                <button onClick={() => { setShowNotifPanel(!showNotifPanel); if (!showNotifPanel) markAllRead(); }} className="relative p-2 rounded-lg hover:bg-white/5 transition-colors" data-testid="notification-bell">
                  <Bell className="w-5 h-5 text-gray-400" />
                  {unreadCount > 0 && <span className="absolute -top-0.5 -right-0.5 bg-fire-500 text-white text-xs w-5 h-5 rounded-full flex items-center justify-center font-bold animate-pulse">{unreadCount > 9 ? '9+' : unreadCount}</span>}
                </button>
                {showNotifPanel && (
                  <div className="absolute right-0 top-12 w-[calc(100vw-2rem)] sm:w-96 bg-[#1A1A1A] border border-white/10 rounded-xl shadow-2xl max-h-[70vh] overflow-hidden z-50" data-testid="notification-panel">
                    <div className="flex items-center justify-between px-4 py-3 border-b border-white/10">
                      <h3 className="text-white font-bold text-sm">Notifications</h3>
                      <div className="flex items-center gap-2">
                        {notifications.length > 0 && <button onClick={() => setNotifications([])} className="text-gray-500 text-xs hover:text-white">Clear</button>}
                        <button onClick={() => setShowNotifPanel(false)} className="text-gray-500 hover:text-white"><X className="w-4 h-4" /></button>
                      </div>
                    </div>
                    <div className="overflow-y-auto max-h-[60vh]">
                      {notifications.length === 0 ? (
                        <div className="p-8 text-center"><Bell className="w-10 h-10 text-gray-600 mx-auto mb-3" /><p className="text-gray-500 text-sm">No notifications yet</p></div>
                      ) : notifications.map(notif => (
                        <div key={notif.id} className={`px-4 py-3 border-b border-white/5 hover:bg-white/5 ${!notif.read ? 'bg-fire-500/5' : ''}`}>
                          <div className="flex items-start gap-3">
                            <div className="w-8 h-8 rounded-full bg-fire-500/20 flex items-center justify-center flex-shrink-0 mt-0.5"><ShoppingBag className="w-4 h-4 text-fire-500" /></div>
                            <div className="flex-1 min-w-0">
                              <p className="text-white text-sm font-semibold">Order #{notif.order_number}</p>
                              <p className="text-gray-400 text-xs">{notif.customer} - {notif.items_count} items</p>
                              <p className="text-fire-500 font-bold text-sm mt-1">₦{notif.total?.toLocaleString()}</p>
                              <div className="flex gap-2 mt-2">
                                <Link to={`/admin/orders/${notif.order_id}`} className="text-xs text-blue-400" onClick={() => setShowNotifPanel(false)}>View</Link>
                                {notif.whatsapp_url && <a href={notif.whatsapp_url} target="_blank" rel="noopener noreferrer" className="text-xs text-[#25D366] flex items-center gap-1"><MessageCircle className="w-3 h-3" />WhatsApp</a>}
                              </div>
                            </div>
                            <div className="flex flex-col items-end gap-1">
                              <span className="text-gray-500 text-xs" title={formatFullTimestamp(notif)}>{formatNotifTime(notif)}</span>
                              <button onClick={() => dismissNotif(notif.id)} className="text-gray-600 hover:text-gray-400"><X className="w-3 h-3" /></button>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
              <button onClick={() => playSound()} className="p-2 rounded-lg hover:bg-white/5 hidden sm:block" title="Test sound" data-testid="test-sound-btn"><Volume2 className="w-5 h-5 text-gray-400" /></button>
              {!pushEnabled ? (
                <button onClick={enablePushNotifications} disabled={pushLoading} className="p-2 rounded-lg hover:bg-white/5 hidden sm:block group relative" title="Enable device push notifications" data-testid="enable-push-btn">
                  <Smartphone className={`w-5 h-5 ${pushLoading ? 'text-yellow-400 animate-pulse' : 'text-gray-400 group-hover:text-fire-500'}`} />
                </button>
              ) : (
                <div className="p-2 hidden sm:block" title="Push notifications enabled">
                  <Smartphone className="w-5 h-5 text-green-400" data-testid="push-enabled-icon" />
                </div>
              )}
              <Button variant="ghost" onClick={handleLogout} className="text-gray-400 hover:text-red-400 hover:bg-red-500/10 h-9 px-2 md:px-3 text-sm hidden sm:flex" data-testid="header-logout-btn">
                <LogOut className="w-4 h-4 md:mr-2" /><span className="hidden md:inline">Sign Out</span>
              </Button>
            </div>
          </div>
        </header>

        <div className="p-4 md:p-6 lg:p-8">{children}</div>
      </main>

      {/* Click outside notif panel */}
      {showNotifPanel && <div className="fixed inset-0 z-[5]" onClick={() => setShowNotifPanel(false)} />}

      {/* Order Popup */}
      {orderPopup && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4" data-testid="order-popup-overlay">
          <div className="bg-[#1A1A1A] border-2 border-fire-500 rounded-2xl shadow-[0_0_60px_rgba(255,69,0,0.3)] w-full max-w-lg overflow-hidden" data-testid="order-popup">
            <div className="bg-fire-500 px-4 md:px-6 py-4 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center"><ShoppingBag className="w-5 h-5 text-white" /></div>
                <div>
                  <h2 className="text-white font-bold text-lg">New Order!</h2>
                  <p className="text-white/80 text-sm">#{orderPopup.order_number}</p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-white/60 text-xs flex items-center gap-1"><Clock className="w-3 h-3" />{orderPopup.time || new Date().toLocaleTimeString()}</span>
                <button onClick={() => setOrderPopup(null)} className="text-white/80 hover:text-white p-1" data-testid="close-order-popup"><X className="w-6 h-6" /></button>
              </div>
            </div>
            <div className="px-4 md:px-6 py-5 space-y-4">
              <div className="flex items-center gap-3"><User className="w-5 h-5 text-gray-400" /><div><p className="text-white font-semibold">{orderPopup.customer}</p><p className="text-gray-400 text-sm">{orderPopup.customer_phone}</p></div></div>
              <div className="bg-black/30 rounded-lg p-3">
                <p className="text-gray-400 text-xs uppercase mb-2">Items ({orderPopup.items_count})</p>
                {orderPopup.items?.slice(0, 5).map((item, i) => (<div key={i} className="flex justify-between text-sm py-1"><span className="text-white">{item.quantity}x {item.name}</span><span className="text-gray-400">₦{(item.price * item.quantity).toLocaleString()}</span></div>))}
              </div>
              <div className="flex justify-between items-center pt-2 border-t border-white/10"><span className="text-gray-400">Total</span><span className="text-fire-500 font-bold text-2xl">₦{orderPopup.total?.toLocaleString()}</span></div>
            </div>
            <div className="px-4 md:px-6 py-4 bg-black/20 border-t border-white/10 flex flex-col sm:flex-row gap-2">
              <Link to={`/admin/orders/${orderPopup.order_id}`} onClick={() => setOrderPopup(null)} className="flex-1">
                <Button className="w-full bg-fire-500 hover:bg-fire-600 text-white rounded-lg" data-testid="popup-view-order"><ShoppingBag className="w-4 h-4 mr-2" />View Order</Button>
              </Link>
              {orderPopup.whatsapp_url && <a href={orderPopup.whatsapp_url} target="_blank" rel="noopener noreferrer" className="flex-1"><Button className="w-full bg-[#25D366] hover:bg-[#128C7E] text-white rounded-lg"><MessageCircle className="w-4 h-4 mr-2" />WhatsApp</Button></a>}
              <Button variant="outline" onClick={() => setOrderPopup(null)} className="border-white/10 text-gray-400 hover:bg-white/10 rounded-lg">Dismiss</Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
